import React, { useMemo, useRef, useState } from "react";
import { useTheme } from "../../../../Utils/useTheme";
import { Column, FilterSpec, SortSpec } from "../types";
import { FilterPopover } from "./FilterPopover";
import { ColumnSettingsPanel } from "./ColumnSettingsPanel";
import { getColumnStyle } from "../utils/getColumnStyle";

const Icon = {
  filter: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 6h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M7 12h10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10 18h4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  columns: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M4 5h6v14H4V5Z" stroke="currentColor" strokeWidth="2" />
      <path d="M14 5h6v14h-6V5Z" stroke="currentColor" strokeWidth="2" />
    </svg>
  ),
  sortNone: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M8 9l4-4 4 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 15l-4 4-4-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  sortAsc: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M8 15l4 4 4-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 5v14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  sortDesc: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M8 9l4-4 4 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 5v14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
};

const iconBtn = (border: string) => ({
  width: 30,
  height: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 10,
  border: `1px solid ${border}`,
  background: "rgba(17,24,39,0.06)",
  cursor: "pointer",
  userSelect: "none",
});

export const TableHeader = ({
  columns,
  visibleColumns,
  sorts,
  filters,
  data,
  onToggleSort,
  onOpenFilter,
  activeFilterKey,
  onFilterChange,
  onCloseFilter,
  onToggleHidden,
  hiddenMap,
}: {
  columns: Column<any>[];
  visibleColumns: Column<any>[];
  sorts: SortSpec[];
  filters: FilterSpec[];
  data: any[];
  onToggleSort: (key: string) => void;
  onOpenFilter: (key: string) => void;
  activeFilterKey: string | null;
  onFilterChange: (next: FilterSpec | null) => void;
  onCloseFilter: () => void;
  onToggleHidden: (key: string) => void;
  hiddenMap: Record<string, boolean>;
}) => {
  const { border, text, hover } = useTheme();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsRect, setSettingsRect] = useState<DOMRect | null>(null);
  const [filterRect, setFilterRect] = useState<DOMRect | null>(null);

  const settingsBtnRef = useRef<HTMLDivElement | null>(null);
  const filterBtnRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const filterMap = useMemo(() => {
    const m = new Map<string, FilterSpec>();
    for (const f of filters || []) m.set(f.key, f);
    return m;
  }, [filters]);

  const sortMap = useMemo(() => {
    const m = new Map<string, SortSpec>();
    for (const s of sorts || []) m.set(s.key, s);
    return m;
  }, [sorts]);

  const openFilter = (key: string) => {
    const el = filterBtnRefs.current[key];
    setFilterRect(el ? el.getBoundingClientRect() : null);
    onOpenFilter(key);
  };

  return (
    <thead>
      <tr>
        {visibleColumns.map((c) => {
          const s = sortMap.get(c.key);
          const f = filterMap.get(c.key);

          return (
            <th
              key={c.key}
              style={{
                ...getColumnStyle(c),
                position: "sticky",
                top: 0,
                zIndex: 50,
                background: "#52efcc",
                borderBottom: `1px solid ${border}`,
                padding: "0 5px",
                color: text,
                fontSize: 12,
                fontWeight: 600,
                userSelect: "none",
                cursor: c.sortable ? "pointer" : "default",
                textAlign: "left",
                verticalAlign: "middle",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as any).style.background = hover) as any
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as any).style.background = "#52efcc") as any
              }
              onClick={() => {
                if (c.sortable) onToggleSort(c.key);
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.title}
                </div>

                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {c.sortable && (
                    <div style={{ opacity: s ? 1 : 0.45, display: "flex" }}>
                      {s ? (
                        s.dir === "asc" ? (
                          <Icon.sortAsc />
                        ) : (
                          <Icon.sortDesc />
                        )
                      ) : (
                        <Icon.sortNone />
                      )}
                    </div>
                  )}

                  {c.filterable && (
                    <div
                      ref={(el) => {
                        filterBtnRefs.current[c.key] = el;
                      }}
                      style={{ ...iconBtn(border), opacity: f ? 1 : 0.55 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (activeFilterKey === c.key) onCloseFilter();
                        else openFilter(c.key);
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = hover)
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(17,24,39,0.06)")
                      }
                    >
                      <Icon.filter />
                    </div>
                  )}
                </div>

                {activeFilterKey === c.key && c.filterable && (
                  <FilterPopover
                    anchorRect={filterRect}
                    column={c}
                    value={filterMap.get(c.key)}
                    onChange={onFilterChange}
                    onClose={onCloseFilter}
                  />
                )}
              </div>
            </th>
          );
        })}

        <th
          style={{
            position: "sticky",
            top: 0,
            zIndex: 55,
            background: "#52efcc",
            borderBottom: `1px solid ${border}`,
            padding: "0 10px",
            textAlign: "right",
            verticalAlign: "middle",
            width: 52,
            minWidth: 52,
            maxWidth: 52,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
            }}
          >
            <div
              ref={settingsBtnRef}
              style={iconBtn(border)}
              onMouseEnter={(e) => (e.currentTarget.style.background = hover)}
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "rgba(17,24,39,0.06)")
              }
              onClick={(e) => {
                e.stopPropagation();
                const rect = settingsBtnRef.current
                  ? settingsBtnRef.current.getBoundingClientRect()
                  : null;
                setSettingsRect(rect);
                setSettingsOpen((v) => !v);
              }}
            >
              <Icon.columns />
            </div>

            {settingsOpen && (
              <ColumnSettingsPanel
                anchorRect={settingsRect}
                columns={columns}
                hidden={hiddenMap}
                onToggleHidden={onToggleHidden}
                onClose={() => setSettingsOpen(false)}
              />
            )}
          </div>
        </th>
      </tr>
    </thead>
  );
};
